<template>
  <Message
    v-bind="$attrs"
    :severity="props.severity"
    :pt="{
      root: rootClass,
      icon: iconClass,
      text: textClass,
    }"
  >
    <slot />
  </Message>
</template>

<script setup lang="ts">
import { Message } from 'primevue';
import { computed } from 'vue';

interface Props {
  severity?: 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast';
  variant?: 'default' | 'soft';
}

const props = withDefaults(defineProps<Props>(), {
  severity: 'info',
  variant: 'soft',
});

const rootClass = computed(() => {
  if (props.variant === 'soft') {
    // Soft variant with semi-transparent backgrounds
    switch (props.severity) {
      case 'error':
        return 'bg-red-900/20 border border-red-500/30 text-red-300';
      case 'success':
        return 'bg-green-900/20 border border-green-500/30 text-green-300';
      case 'warn':
        return 'bg-yellow-900/20 border border-yellow-500/30 text-yellow-300';
      case 'info':
        return 'bg-blue-900/20 border border-blue-500/30 text-blue-300';
      case 'secondary':
        return 'bg-surface-800/60 border border-surface-600/50 text-surface-100';
      default:
        return 'bg-surface-900/80 border border-surface-700 text-surface-100';
    }
  } else {
    // Default variant with solid backgrounds
    switch (props.severity) {
      case 'error':
        return 'bg-red-600 border border-red-700 text-white';
      case 'success':
        return 'bg-green-600 border border-green-700 text-white';
      case 'warn':
        return 'bg-yellow-600 border border-yellow-700 text-white';
      case 'info':
        return 'bg-blue-600 border border-blue-700 text-white';
      case 'secondary':
        return 'bg-surface-700 border border-surface-600 text-surface-100';
      default:
        return 'bg-surface-800 border border-surface-700 text-surface-100';
    }
  }
});

const iconClass = computed(() => {
  if (props.variant === 'soft') {
    switch (props.severity) {
      case 'error':
        return 'text-red-400';
      case 'success':
        return 'text-green-400';
      case 'warn':
        return 'text-yellow-400';
      case 'info':
        return 'text-blue-400';
      default:
        return 'text-surface-400';
    }
  } else {
    return 'text-white';
  }
});

const textClass = computed(() => {
  if (props.variant === 'soft') {
    switch (props.severity) {
      case 'error':
        return 'text-red-300';
      case 'success':
        return 'text-green-300';
      case 'warn':
        return 'text-yellow-300';
      case 'info':
        return 'text-blue-300';
      default:
        return 'text-surface-100';
    }
  } else {
    return 'text-white';
  }
});
</script>

<style scoped></style>
